import { Button, Size, TooltipTrigger } from '@toggle/design-system';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { copy } from '~/components/copy-to-clipboard/utils/copy';
import { TooltipContainer } from '~/components/tooltip-container/TooltipContainer';
import { Tracking } from '~/services/tracking';

import { useConversationInventory } from '../hooks/useConversationInventory';
import { useFormattedInventory } from '../hooks/useFormattedInventory';
import { ConversationTask } from '../use-chat/use-chat.types';
import { useVisualizationStore } from '../use-visualization-store/useVisualizationStore';
import { copyResearchMessage } from '../utils/copyResearchMessage';

interface CopyConversationProps {
  task: ConversationTask;
  questionOnly: boolean;
  showTooltip?: boolean;
  size?: Size;
  showingSteps?: boolean;
  conversationId?: string;
}

export const CopyConversation = ({
  task,
  questionOnly,
  showingSteps = false,
  showTooltip = true,
  size = 'small',
  conversationId,
}: CopyConversationProps) => {
  const { t } = useTranslation(['common', 'chat']);

  const [isCopied, setIsCopied] = useState(false);

  const { data: inventory } = useConversationInventory(
    conversationId || '',
    !!conversationId
  );

  const formattedInventory = useFormattedInventory(inventory);

  const { charts, tables } = useVisualizationStore(state => ({
    charts: state.charts,
    tables: state.tables,
  }));

  const handleCopy = async () => {
    try {
      if (questionOnly) {
        copy(task.input, task.input);
      } else {
        const states = task.states;

        if (!states) {
          return;
        }

        await copyResearchMessage({
          task: showingSteps
            ? task
            : { ...task, states: [states[states.length - 1]] },
          charts,
          tables,
          inventory: formattedInventory,
          t,
        });
      }

      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2_000);
    } catch (error) {
      Tracking.captureException(error);
    }
  };

  return (
    <TooltipContainer
      label={t('common:copyTooltip')}
      inPortal
      trigger={[TooltipTrigger.Hover]}
      disabled={!showTooltip}
    >
      <Button
        onClick={handleCopy}
        variant="empty"
        size={size}
        iconName={isCopied ? 'Checkmark' : 'Clone'}
      />
    </TooltipContainer>
  );
};
